import { type GraphQLResponse, Observable } from 'relay-runtime';
import type { Sink } from 'relay-runtime/lib/network/RelayObservable';

/**
 * Server-injected GraphQL response cache.
 *
 * The Volt controller (or any other server-side query executor) emits inline
 * `<script>` tags that push GraphQL responses onto a global preload queue.
 * This module owns that queue and turns it into a lookup the Relay
 * environment's network layer consults before doing any HTTP fetch.
 *
 * Each cache entry is a buffered Observable + externally-pullable Sink, so
 * the primitive supports late producers (consumer subscribes before producer
 * pushes data) and streaming responses (multi-chunk @stream/@defer) — not
 * just the single-shot bootstrap case used today.
 *
 * Server-side script tag shape:
 *   <script>
 *     (globalThis.__TOWNSQUARE_RELAY_PRELOAD_QUEUE__ ||= []).push({
 *       operationKey: "<cacheID>{...stable variables JSON}",
 *       response: { data: { ... } }
 *     });
 *   </script>
 *
 * `operationKey` must match `getRequestIdentifier(request, variables)` from
 * `relay-runtime` —
 *   `(request.cacheID ?? request.id) + JSON.stringify(stableCopy(variables))`.
 */

type PreloadedResponse = {
	observable: Observable<GraphQLResponse>;
	sink: Sink<GraphQLResponse>;
};

class RelayPreloadedResponseStreamHandler {
	cache: Map<string, PreloadedResponse> = new Map();

	getSink(operationKey: string): Sink<GraphQLResponse> | undefined {
		return this.cache.get(operationKey)?.sink;
	}

	getObservable(operationKey: string): Observable<GraphQLResponse> | undefined {
		return this.cache.get(operationKey)?.observable;
	}

	create(operationKey: string): Sink<GraphQLResponse> {
		if (this.cache.has(operationKey)) {
			throw new Error(`Operation ${operationKey} already exists in cache`);
		}

		let capturedSink: Sink<GraphQLResponse> | null = null;
		const bufferedEvents: Array<
			{ kind: 'next'; value: GraphQLResponse } | { kind: 'error'; error: Error } | { kind: 'complete' }
		> = [];
		let isClosed = false;

		const sink: Sink<GraphQLResponse> = {
			next: (value: GraphQLResponse) => {
				if (isClosed) {
					return;
				}
				if (capturedSink) {
					capturedSink.next(value);
				} else {
					bufferedEvents.push({ kind: 'next', value });
				}
			},
			error: (error: Error) => {
				if (isClosed) {
					return;
				}
				isClosed = true;
				if (capturedSink) {
					capturedSink.error(error);
				} else {
					bufferedEvents.push({ kind: 'error', error });
				}
			},
			complete: () => {
				if (isClosed) {
					return;
				}
				isClosed = true;
				if (capturedSink) {
					capturedSink.complete();
				} else {
					bufferedEvents.push({ kind: 'complete' });
				}
			},
			closed: false,
		};

		const observable = Observable.create<GraphQLResponse>(innerSink => {
			capturedSink = innerSink;

			// Flush buffered events in order.
			for (const event of bufferedEvents) {
				if (innerSink.closed) break;
				switch (event.kind) {
					case 'next':
						innerSink.next(event.value);
						break;
					case 'error':
						innerSink.error(event.error);
						break;
					case 'complete':
						innerSink.complete();
						break;
				}
			}

			// After subscription, swap wrapper behaviors to forward directly.
			sink.next = (value: GraphQLResponse) => {
				if (!innerSink.closed) innerSink.next(value);
			};
			sink.error = (error: Error) => {
				if (!innerSink.closed) {
					isClosed = true;
					innerSink.error(error);
				}
			};
			sink.complete = () => {
				if (!innerSink.closed) {
					isClosed = true;
					innerSink.complete();
				}
			};
		});

		this.cache.set(operationKey, { observable, sink });
		return sink;
	}
}

// FIXME: We really, really don't like initialisation at the module level.
const RelayPreloadedResponseStream: RelayPreloadedResponseStreamHandler = new RelayPreloadedResponseStreamHandler();

/**
 * Server-side handoff queue.
 *
 * The server's only handoff to the client is the HTML response itself — it
 * writes inline `<script>` blobs that push entries onto a `globalThis` queue.
 * This module owns that queue: on module init it drains any entries the
 * server already pushed (script tags ran before the bundle parsed), and it
 * replaces the queue with a live forwarder so any later script tags push
 * straight into the cache.
 */
type PreloadQueueEntry = { operationKey: string; response: GraphQLResponse };

declare global {
	// eslint-disable-next-line no-var
	var __TOWNSQUARE_RELAY_PRELOAD_QUEUE__: PreloadQueueEntry[] | { push(entry: PreloadQueueEntry): void } | undefined;
}

function ingest(entry: PreloadQueueEntry): void {
	if (RelayPreloadedResponseStream.cache.has(entry.operationKey)) {
		return;
	}
	const sink = RelayPreloadedResponseStream.create(entry.operationKey);
	sink.next(entry.response);
	sink.complete();
}

const existing = globalThis.__TOWNSQUARE_RELAY_PRELOAD_QUEUE__;
if (Array.isArray(existing)) {
	for (const entry of existing) {
		ingest(entry);
	}
}
globalThis.__TOWNSQUARE_RELAY_PRELOAD_QUEUE__ = { push: ingest };

export default RelayPreloadedResponseStream;
